import { Blackjack2BetType } from '@/models/games/blackjack2';
import { GameFeature as GameFeatureEnum } from '@/models/games/enums/GameFeature';
import { GameType } from '@/models/games/enums/GameType';
import { BetPayload } from '@/modules/betTable/payload';
import {
    getTableBetByHostId,
    TableBet,
    tableBetSliceActions,
} from '@/modules/betTable/slice';
import { getLobbyStateHallGameTypeMap } from '@/modules/lobby/selector';
import { getMainPlayerState } from '@/modules/main/selector';
import { LiveDataSliceActions } from '@/modules/main/slice/liveData';
import {
    BJ2BetSummary,
    bj2SeatSliceActions,
    getEmptyBJ2BetSummary,
} from '@/modules/rwdGame/blackjack2/slice/seatSlice';
import { RootState } from '@/store/store';
import { isBitActive } from '@/utils/bitwiseUtil';
import { calcLowHigh, getWithHold } from '@/utils/commonFunc';
import { AnyAction } from 'redux';
import { Epic } from 'redux-observable';
import { concat, filter, switchMap } from 'rxjs';

export const clearPendingEpic: Epic = (action$, store$) =>
    action$.pipe(
        filter(tableBetSliceActions.clearPendingBets.match), //For ABC only
        switchMap(action => {
            return concat(
                commonAction(
                    action.type,
                    {
                        hostId: action.payload.hostId,
                        gameId: action.payload.gameId,
                        bets: [],
                    },
                    store$.value as unknown as RootState
                )
            );
        })
    );

export const initBetEpic: Epic = (action$, store$) =>
    action$.pipe(
        filter(tableBetSliceActions.initBets.match), //For ABC only
        switchMap(action => {
            return concat(
                commonAction(
                    action.type,
                    {
                        hostId: action.payload.hostId,
                        gameId: action.payload.gameId,
                        bets: [],
                    },
                    store$.value as unknown as RootState
                )
            );
        })
    );

export const addBetEpic: Epic = (action$, store$) =>
    action$.pipe(
        filter(tableBetSliceActions.addBet.match),
        switchMap(action => {
            return concat(
                commonAction(
                    action.type,
                    action.payload,
                    store$.value as unknown as RootState
                )
            );
        })
    );

export const undoBetEpic: Epic = (action$, store$) =>
    action$.pipe(
        filter(tableBetSliceActions.undoBet.match),
        switchMap(action => {
            return concat(
                commonAction(
                    action.type,
                    action.payload,
                    store$.value as unknown as RootState
                )
            );
        })
    );

export const bjStandBetEpic: Epic = (action$, store$) =>
    action$.pipe(
        filter(tableBetSliceActions.doBJHandleStandBets.match),
        switchMap(action => {
            return concat(
                commonAction(
                    action.type,
                    {
                        hostId: action.payload.hostId,
                        gameId: action.payload.gameId,
                        bets: action.payload.playerBets.length
                            ? action.payload.playerBets[0].bets
                            : [],
                    },
                    store$.value as unknown as RootState
                )
            );
        })
    );

export const updateConfirmedBetsEpic: Epic = (action$, store$) =>
    action$.pipe(
        filter(tableBetSliceActions.updateConfirmedBets.match),
        switchMap(action => {
            return concat(
                commonAction(
                    action.type,
                    {
                        hostId: action.payload.hostId,
                        gameId: action.payload.gameId,
                        bets: [],
                    },
                    store$.value as unknown as RootState
                )
            );
        })
    );
export const startBJBetEpic: Epic = (action$, store$) =>
    action$.pipe(
        filter(tableBetSliceActions.startBJBet.match),
        switchMap(action => {
            return concat(
                commonAction(
                    action.type,
                    {
                        hostId: action.payload.hostId,
                        gameId: action.payload.gameId,
                        bets: [],
                    },
                    store$.value as unknown as RootState
                )
            );
        })
    );

const commonAction = (type: string, payload: BetPayload, store: RootState) => {
    const outs = [] as Array<AnyAction>;
    const hostId = payload.hostId;
    const gameId = payload.gameId;
    // const store = store$.value as unknown as RootState;
    const hosts = getLobbyStateHallGameTypeMap(store);
    const host = hosts?.find(v => v.hostId === hostId);
    const withHold = host ? getWithHold(host.gameType) : undefined;
    let _totalWithHoldAmount = 0;
    let _pendingWithHoldAmount = 0;
    if (type == 'app::bet::table/updateConfirmedBets') {
        if (host && host.gameType !== GameType.Blackjack2) {
            return outs;
        }
    }
    const storeTB = (
        store ? getTableBetByHostId(store, hostId) : undefined
    ) as TableBet;
    const tb: TableBet = {
        HostId: hostId,
        GameId: gameId,
        ReadySync: storeTB?.GameId === gameId ? storeTB.ReadySync : 0,
        BetSeqId: storeTB?.GameId === gameId ? storeTB.BetSeqId : 0,
        PlayerBets:
            storeTB?.GameId === gameId &&
            type !== 'app::bet::table/clearPendingBets'
                ? storeTB?.PlayerBets
                : [],
        BetsSummary: [],
        PendingBets: [],
        ConfirmedBets:
            storeTB?.GameId === gameId && storeTB?.ConfirmedBets
                ? storeTB.ConfirmedBets
                : [],
        TableTotalBet: 0,
        PendingTotalBet: 0,
        isWaitingBet: false,
        isBetConfirmed:
            storeTB?.GameId === gameId && storeTB?.isBetConfirmed
                ? storeTB.isBetConfirmed
                : false,
    };
    let minAmount = 0;
    let pendingBets = storeTB ? [...storeTB.PendingBets] : []; //for non-ABC
    if (type !== 'app::bet::table/clearPendingBets') {
        if (type == 'app::bet::table/addBet') {
            payload.bets.forEach(bet => {
                if (!pendingBets[bet.Type]) {
                    pendingBets[bet.Type] = bet.Amount;
                } else {
                    pendingBets[bet.Type] += bet.Amount;
                }
            });
        } else if (type == 'app::bet::table/undoBet') {
            payload.bets.forEach(bet => {
                if (pendingBets[bet.Type] > bet.Amount) {
                    pendingBets[bet.Type] -= bet.Amount;
                } else {
                    pendingBets[bet.Type] = 0;
                }
            });
        } else if (type == 'app::bet::table/doBJHandleStandBets') {
            pendingBets = [];
            tb.PlayerBets.forEach(betGroup => {
                for (let i = 0; i < betGroup.bets.length; i++) {
                    if (!pendingBets[betGroup.bets[i].Type]) {
                        pendingBets[betGroup.bets[i].Type] =
                            betGroup.bets[i].Amount;
                    } else {
                        pendingBets[betGroup.bets[i].Type] +=
                            betGroup.bets[i].Amount;
                    }
                }
            });
        }
        tb.PendingBets = pendingBets;

        let betsum = new Array<number>();
        tb.PlayerBets.forEach(betGroup => {
            for (let i = 0; i < betGroup.bets.length; i++) {
                if (!betsum[betGroup.bets[i].Type]) {
                    betsum[betGroup.bets[i].Type] = betGroup.bets[i].Amount;
                } else {
                    betsum[betGroup.bets[i].Type] += betGroup.bets[i].Amount;
                }
            }
        });
        tb.BetsSummary = betsum;
        if (type == 'app::bet::table/initBets') {
            tb.PendingBets = [...betsum];
        }
        betsum.forEach(amount => {
            if (amount) {
                if (minAmount == 0 || amount < minAmount) {
                    minAmount = amount;
                }
            }
        });
        tb.minAmount = minAmount;
    } else {
        pendingBets = [];
    }

    let BJ2betSummary = [] as Array<BJ2BetSummary>;

    if (type !== 'app::bet::table/clearPendingBets') {
        tb.BetsSummary.forEach((amount, index) => {
            if (amount) {
                if (!tb.isBetConfirmed) {
                    tb.TableTotalBet += amount;
                    if (withHold) {
                        const tmp = withHold.find(v => v.Type == index);
                        if (tmp) {
                            _totalWithHoldAmount += amount * tmp.Amount;
                        }
                    }
                    if (host && host.gameType === GameType.Blackjack2) {
                        const seat = index % 10;
                        if (!BJ2betSummary[seat])
                            BJ2betSummary[seat] = getEmptyBJ2BetSummary();

                        switch (index) {
                            case Blackjack2BetType.BJB1_Split1:
                            case Blackjack2BetType.BJB2_Split1:
                            case Blackjack2BetType.BJB3_Split1:
                            case Blackjack2BetType.BJB4_Split1:
                            case Blackjack2BetType.BJB5_Split1:
                            case Blackjack2BetType.BJB6_Split1:
                            case Blackjack2BetType.BJB7_Split1:
                                BJ2betSummary[seat].MainBet += amount;
                                BJ2betSummary[seat].isMe = true;
                                break;
                            case Blackjack2BetType.BJBB1_Split1:
                            case Blackjack2BetType.BJBB2_Split1:
                            case Blackjack2BetType.BJBB3_Split1:
                            case Blackjack2BetType.BJBB4_Split1:
                            case Blackjack2BetType.BJBB5_Split1:
                            case Blackjack2BetType.BJBB6_Split1:
                            case Blackjack2BetType.BJBB7_Split1:
                                BJ2betSummary[seat].BetBehind += amount;
                                break;
                            case Blackjack2BetType.BJB1_Pair:
                            case Blackjack2BetType.BJB2_Pair:
                            case Blackjack2BetType.BJB3_Pair:
                            case Blackjack2BetType.BJB4_Pair:
                            case Blackjack2BetType.BJB5_Pair:
                            case Blackjack2BetType.BJB6_Pair:
                            case Blackjack2BetType.BJB7_Pair:
                                BJ2betSummary[seat].Pair += amount;
                                //Pair
                                // outs.push(
                                //     bj2SeatSliceActions.updateSeatObject({
                                //         hostId: hostId,
                                //         seat: index % 10,
                                //         PairBet: amount ? amount : 0,
                                //     })
                                // );
                                break;
                        }
                    }
                }
            }
        });
    }
    tb.ConfirmedBets.forEach((amount, index) => {
        if (amount) {
            tb.TableTotalBet += amount;
            if (withHold) {
                const tmp = withHold.find(v => v.Type == index);
                if (tmp) {
                    _totalWithHoldAmount += amount * tmp.Amount;
                }
            }
            if (host && host.gameType === GameType.Blackjack2) {
                if (tb.isBetConfirmed) {
                    const seat: number = index % 10;
                    if (!BJ2betSummary[seat])
                        BJ2betSummary[seat] = getEmptyBJ2BetSummary();

                    if (index - seat === Blackjack2BetType.BJBB_Split2_Start) {
                        BJ2betSummary[seat].IsBetBehindSplit = true;
                    }

                    switch (index) {
                        case Blackjack2BetType.BJB1_Split1:
                        case Blackjack2BetType.BJB2_Split1:
                        case Blackjack2BetType.BJB3_Split1:
                        case Blackjack2BetType.BJB4_Split1:
                        case Blackjack2BetType.BJB5_Split1:
                        case Blackjack2BetType.BJB6_Split1:
                        case Blackjack2BetType.BJB7_Split1:
                        case Blackjack2BetType.BJB1_Split2:
                        case Blackjack2BetType.BJB2_Split2:
                        case Blackjack2BetType.BJB3_Split2:
                        case Blackjack2BetType.BJB4_Split2:
                        case Blackjack2BetType.BJB5_Split2:
                        case Blackjack2BetType.BJB6_Split2:
                        case Blackjack2BetType.BJB7_Split2:
                        case Blackjack2BetType.BJB1_DoubleDown_H1:
                        case Blackjack2BetType.BJB2_DoubleDown_H1:
                        case Blackjack2BetType.BJB3_DoubleDown_H1:
                        case Blackjack2BetType.BJB4_DoubleDown_H1:
                        case Blackjack2BetType.BJB5_DoubleDown_H1:
                        case Blackjack2BetType.BJB6_DoubleDown_H1:
                        case Blackjack2BetType.BJB7_DoubleDown_H1:
                        case Blackjack2BetType.BJB1_DoubleDown_H2:
                        case Blackjack2BetType.BJB2_DoubleDown_H2:
                        case Blackjack2BetType.BJB3_DoubleDown_H2:
                        case Blackjack2BetType.BJB4_DoubleDown_H2:
                        case Blackjack2BetType.BJB5_DoubleDown_H2:
                        case Blackjack2BetType.BJB6_DoubleDown_H2:
                        case Blackjack2BetType.BJB7_DoubleDown_H2:
                            BJ2betSummary[seat].MainBet += amount;
                            BJ2betSummary[seat].isMe = true;
                            break;
                        case Blackjack2BetType.BJBB1_Split1:
                        case Blackjack2BetType.BJBB2_Split1:
                        case Blackjack2BetType.BJBB3_Split1:
                        case Blackjack2BetType.BJBB4_Split1:
                        case Blackjack2BetType.BJBB5_Split1:
                        case Blackjack2BetType.BJBB6_Split1:
                        case Blackjack2BetType.BJBB7_Split1:
                        case Blackjack2BetType.BJBB1_Split2:
                        case Blackjack2BetType.BJBB2_Split2:
                        case Blackjack2BetType.BJBB3_Split2:
                        case Blackjack2BetType.BJBB4_Split2:
                        case Blackjack2BetType.BJBB5_Split2:
                        case Blackjack2BetType.BJBB6_Split2:
                        case Blackjack2BetType.BJBB7_Split2:
                        case Blackjack2BetType.BJBB1_DoubleDown_H1:
                        case Blackjack2BetType.BJBB2_DoubleDown_H1:
                        case Blackjack2BetType.BJBB3_DoubleDown_H1:
                        case Blackjack2BetType.BJBB4_DoubleDown_H1:
                        case Blackjack2BetType.BJBB5_DoubleDown_H1:
                        case Blackjack2BetType.BJBB6_DoubleDown_H1:
                        case Blackjack2BetType.BJBB7_DoubleDown_H1:
                        case Blackjack2BetType.BJBB1_DoubleDown_H2:
                        case Blackjack2BetType.BJBB2_DoubleDown_H2:
                        case Blackjack2BetType.BJBB3_DoubleDown_H2:
                        case Blackjack2BetType.BJBB4_DoubleDown_H2:
                        case Blackjack2BetType.BJBB5_DoubleDown_H2:
                        case Blackjack2BetType.BJBB6_DoubleDown_H2:
                        case Blackjack2BetType.BJBB7_DoubleDown_H2:
                            BJ2betSummary[seat].BetBehind += amount;
                            break;
                        case Blackjack2BetType.BJB1_Pair:
                        case Blackjack2BetType.BJB2_Pair:
                        case Blackjack2BetType.BJB3_Pair:
                        case Blackjack2BetType.BJB4_Pair:
                        case Blackjack2BetType.BJB5_Pair:
                        case Blackjack2BetType.BJB6_Pair:
                        case Blackjack2BetType.BJB7_Pair:
                            BJ2betSummary[seat].Pair += amount;
                            //Pair
                            break;
                    }
                }
            }
        }
    });
    if (host && host.gameType === GameType.Blackjack2) {
        for (let i = 1; i <= 7; i++) {
            const summary = BJ2betSummary[i];
            if (summary) {
                if (summary.isMe) {
                    outs.push(
                        bj2SeatSliceActions.updateSeatObject({
                            hostId: hostId,
                            seat: i,
                            IsMe: summary.MainBet > 0,
                            SeatBet: summary.MainBet,
                            PairBet: summary.Pair,
                            ClearIfMe: false,
                        })
                    );
                } else {
                    outs.push(
                        bj2SeatSliceActions.updateSeatObject({
                            hostId: hostId,
                            seat: i,
                            BetBehind: summary.BetBehind,
                            PairBet: summary.Pair,
                            IsBetBehindSplit: summary.IsBetBehindSplit,
                            ClearIfMe: false,
                        })
                    );
                }
            } else {
                outs.push(
                    bj2SeatSliceActions.updateSeatObject({
                        hostId: hostId,
                        seat: i,
                        BetBehind: 0,
                        PairBet: 0,
                        IsBetBehindSplit: false,
                        IsMe: false,
                        ClearIfMe: true,
                    })
                );
            }
        }
    }

    if (type !== 'app::bet::table/clearPendingBets' && !tb.isBetConfirmed) {
        pendingBets.forEach((amount, index) => {
            if (amount) {
                tb.PendingTotalBet += amount;
                if (withHold) {
                    const tmp = withHold.find(v => v.Type == index);
                    if (tmp) {
                        _pendingWithHoldAmount += amount * tmp.Amount;
                    }
                }
            }
        });
    }

    switch (type) {
        case 'app::bet::table/addBet':
        case 'app::bet::table/undoBet':
        case 'app::bet::table/doBJHandleStandBets':
            tb.BetSeqId += 1;
            break;
    }

    outs.push(tableBetSliceActions.update(tb));
    outs.push(
        LiveDataSliceActions.updateHostBetInfo({
            hostId: hostId,
            totalBetAmount: tb.TableTotalBet,
            pendingBetAmount: tb.PendingTotalBet,
            totalWithHoldAmount: _totalWithHoldAmount,
            pendingWithHoldAmount: _pendingWithHoldAmount,
        })
    );

    if (host && host.gameType === GameType.Baccarat) {
        const { GameFeature } = getMainPlayerState(store);
        const curGameFeature = calcLowHigh(GameFeature);
        const isAutoConfirmEnabled = isBitActive(
            curGameFeature,
            GameFeatureEnum.AutoBetConfirmation
        );
        outs.push(
            LiveDataSliceActions.updateBaccaratBetHistory({
                hostId: hostId,
                gameId: gameId,
                betsSummary:
                    isAutoConfirmEnabled && !tb.isBetConfirmed
                        ? tb.BetsSummary
                        : tb.ConfirmedBets,
            })
        );
    }
    return outs;
};
